import { useCallback, useMemo } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import {
  getFeatureAccessQueryKey,
  useGetFeatureAccess,
} from "~/gen/api/hooks/useGetFeatureAccess"
import { useGrantFeatureAccess } from "~/gen/api/hooks/useGrantFeatureAccess"
import { useRevokeFeatureAccess } from "~/gen/api/hooks/useRevokeFeatureAccess"
import type { FeatureKey as GeneratedFeatureKey } from "~/gen/api/types/FeatureKey"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

export const FEATURE_KEY = {
  AGEC_COMPLIANCE: "agec-compliance",
  ECO_MODULATIONS: "eco-modulations",
} as const

export type FeatureKey = GeneratedFeatureKey

export type FeatureAccessGrant = {
  readonly organizationId: string
  readonly featureKey: FeatureKey
  readonly isActive: boolean
}

type UpdateFeatureAccessGrantInput = {
  readonly featureKey: FeatureKey
  readonly isActive: boolean
}

type UseFeatureAccessResult = {
  readonly data: readonly FeatureAccessGrant[]
  readonly featureKeys: readonly FeatureKey[]
  readonly update: (input: UpdateFeatureAccessGrantInput) => Promise<void>
}

const FEATURE_KEYS = [
  FEATURE_KEY.AGEC_COMPLIANCE,
  FEATURE_KEY.ECO_MODULATIONS,
] as const satisfies readonly FeatureKey[]

export function useFeatureAccess(
  organizationId: string
): UseFeatureAccessResult {
  const queryClient = useQueryClient()
  const { t } = useTranslation("admin")

  const { data: agecFeatureAccessQuery } = useGetFeatureAccess(
    organizationId,
    FEATURE_KEY.AGEC_COMPLIANCE
  )
  const { data: ecoModulationsFeatureAccessQuery } = useGetFeatureAccess(
    organizationId,
    FEATURE_KEY.ECO_MODULATIONS
  )
  const { mutateAsync: grantFeatureAccess } = useGrantFeatureAccess()
  const { mutateAsync: revokeFeatureAccess } = useRevokeFeatureAccess()

  const isAgecComplianceActive =
    agecFeatureAccessQuery?.data.access?.isActive === true
  const isEcoModulationsActive =
    ecoModulationsFeatureAccessQuery?.data.access?.isActive === true

  const data = useMemo(() => {
    const isActiveByFeatureKey: Record<FeatureKey, boolean> = {
      [FEATURE_KEY.AGEC_COMPLIANCE]: isAgecComplianceActive,
      [FEATURE_KEY.ECO_MODULATIONS]: isEcoModulationsActive,
    }

    return FEATURE_KEYS.map((featureKey) => ({
      organizationId,
      featureKey,
      isActive: isActiveByFeatureKey[featureKey],
    }))
  }, [isAgecComplianceActive, isEcoModulationsActive, organizationId])

  const update = useCallback(
    async ({ featureKey, isActive }: UpdateFeatureAccessGrantInput) => {
      const featureAccessRequest = {
        organizationId,
        featureKey,
      }

      try {
        if (isActive === true) {
          await grantFeatureAccess({
            data: featureAccessRequest,
          })
        } else {
          await revokeFeatureAccess({
            data: featureAccessRequest,
          })
        }

        showSuccessNotification({
          message: isActive
            ? t("featureAccess.updateSuccess.granted", {
                defaultValue: "Feature access granted",
              })
            : t("featureAccess.updateSuccess.revoked", {
                defaultValue: "Feature access revoked",
              }),
        })

        await queryClient.invalidateQueries({
          queryKey: getFeatureAccessQueryKey(organizationId, featureKey),
        })
      } catch (error) {
        const normalizedError =
          error instanceof Error ? error : new Error("Unknown error")
        showAxiosError({
          error: normalizedError,
          showNotification: true,
          defaultMessage: t("featureAccess.updateErrorMessage", {
            defaultValue: "An error occurred while updating feature access",
          }),
          notificationTitle: t("featureAccess.updateErrorTitle", {
            defaultValue: "Error",
          }),
        })
      }
    },
    [grantFeatureAccess, organizationId, queryClient, revokeFeatureAccess, t]
  )

  return {
    data,
    featureKeys: FEATURE_KEYS,
    update,
  }
}
